package nl.volleyplanner.web.controller.planner

import kotlinx.browser.document
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.datetime.format
import kotlinx.html.*
import kotlinx.html.dom.create
import kotlinx.html.js.div
import nl.volleyplanner.api.auth.AuthLevel
import nl.volleyplanner.api.backend_jobs.JobStatus
import nl.volleyplanner.api.backend_jobs.JobType
import nl.volleyplanner.api.backend_jobs.NevoboSyncJobDetails
import nl.volleyplanner.api.backend_jobs.NevoboSyncJobDetailsV2
import nl.volleyplanner.web.CoroutineManager
import nl.volleyplanner.web.base.*
import nl.volleyplanner.web.component.backend_jobs.BackendJobManager
import nl.volleyplanner.web.component.backend_jobs.JobFlowComponent
import nl.volleyplanner.web.component.backend_jobs.JobFlowState
import nl.volleyplanner.web.launchAsync
import nl.volleyplanner.web.service.WebUserAndOrganisationService
import nl.volleyplanner.web.util.LocalDateTimeFormatters
import nl.volleyplanner.web.util.SwitchingStateFlow
import nl.volleyplanner.web.util.combineAndLaunch
import org.w3c.dom.HTMLElement

class NevoboImportComponent() : HTMLComponent() {
    private val _initialDummyStateFlow = MutableStateFlow<JobFlowState>(JobFlowState(JobType.NevoboSyncVereniging))
    private val jobTrackerState = SwitchingStateFlow<JobFlowState>(CoroutineManager.RENDER_SCOPE, _initialDummyStateFlow)

    init {
        launchAsync(CoroutineManager.RENDER_SCOPE) { BackendJobManager.getAndMonitorLastJob(JobType.NevoboSyncVereniging) }
            .onComplete { backendJobTracker ->
                if (backendJobTracker != null) {
                    jobTrackerState.updateSource(backendJobTracker.stateFlow)
                }
            }
    }

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        return consumer.div {
            h1 (classes = "display-5") { +"Wedstrijden importeren" }
            p (classes = "lead") {
                +"Met enige regelmaat worden wedstrijden en de laatste wijzigingen automatisch opgehaald bij de Nevobo. "
                +"Op deze pagina is de status van de laatste synchronisatie te zien."
            }
            p (classes = "lead") {
                +"Zijn specifieke wijzigingen nog niet gesynchroniseert? Dan kan je hier ook handmatige een nieuwe synchronisatie starten. "
            }
            div (classes = "mt-2") {
                val latestJobExecution = jobTrackerState.value.jobExecution
                val latestSyncJobDetails = latestJobExecution?.backendJobExecution?.details?.let {
                    NevoboSyncJobDetails.decodeJson(it)
                }
                dl (classes = "row") {
                    dt (classes = "col-4") { +"Laatste sync" }
                    dd (classes = "col-8") {
                        if (latestJobExecution != null) {
                            +latestJobExecution.createdAt.format(LocalDateTimeFormatters.DAY_MONTH_YEAR_TIME_TEXT_SHORT)
                        } else {
                            +"geen gegevens"
                        }
                    }
                    dt (classes = "col-4") { +"Status" }
                    dd (classes = "col-8") {
                        if (latestJobExecution != null && latestJobExecution.status != JobStatus.Finished) {
                            spinner(small = true)
                        }
                        +" ${latestJobExecution?.status?.name ?: "-"}"

                    }
                    dt (classes = "col-4") { +"Resultaat" }
                    dd (classes = "col-8") { +(latestJobExecution?.result?.name ?: "-") }
                    dt (classes = "col-4") { +"Seizoen" }
                    dd (classes = "col-8") {
                        if (latestSyncJobDetails is NevoboSyncJobDetailsV2) {
                            +latestSyncJobDetails.seasonName
                        } else {
                            +"-"
                        }
                    }
                    dt (classes = "col-4") { +"Aantal poules" }
                    dd (classes = "col-8") { +(latestSyncJobDetails?.poulesRetrievedAmount?.toString() ?: "-") }
                    dt (classes = "col-4") { +"Aantal teams*" }
                    dd (classes = "col-8") { +(latestSyncJobDetails?.teamsRetrievedAmount?.toString() ?: "-") }
                    dt (classes = "col-4") { +"Aantal wedstrijden" }
                    dd (classes = "col-8") { +(latestSyncJobDetails?.matchesRetrievedAmount?.toString() ?: "-") }
                }
                p (classes = "text-secondary small") {
                    +"* Aantal teams is inclusief teams uit andere verenigingen binnen de wedstrijden"
                }
                if (WebUserAndOrganisationService.selectedOrganisationState.value == null) {
                    spinner()
                } else {
                    component(
                        JobFlowComponent(
                            JobType.NevoboSyncVereniging, listOf(
                                "vereniging", WebUserAndOrganisationService.selectedOrganisationState.value!!.organisation.slug
                            ), "Nu synchroniseren") {
                                backendJobTracker ->
                            jobTrackerState.updateSource(backendJobTracker.stateFlow)
                        }
                    )
                }
            }
        }
    }

    override suspend fun getState() = listOf(jobTrackerState, WebUserAndOrganisationService.selectedOrganisationState)
        .combineAndLaunch(CoroutineManager.RENDER_SCOPE)
}

class NevoboImportController(override val parent: Controller?) : StaticPageController() {
    override val name = "NevoboImportController"
    override val title = "Importeer wedstrijden"
    override val path = "/importeren/wedstrijden"
    override val requiredAuthLevel = AuthLevel.AdminUser
    override val childPages = emptyList<WebPageController>()

    override fun createRuntimeInstance() = NevoboImportController(parent)

    override suspend fun renderContent(): HTMLElement {
        return document.create.div {
            component(NevoboImportComponent())
        }
    }
}